import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Spinner } from "./Spinner";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "override"
  | "danger"
  | "bare";

export type ButtonSize = "s" | "xs";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Maps to `.btn--s` (page-header CTAs) / `.btn--xs` (dense per-row
   *  actions). Omit for the default size. */
  size?: ButtonSize;
  /** While true, shows an inline spinner before the label and disables the
   *  button. Replaces the old `isPending ? "Loading…" : "Save"` ternaries so
   *  every action button signals work the same way. */
  loading?: boolean;
  children: ReactNode;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: "btn btn--p",
  secondary: "btn",
  ghost: "btn btn--g",
  override: "btn btn--ovr",
  danger: "btn btn--dgr",
  // `bare` emits no `btn-*` class — for clickable containers (cards,
  // tiles) that own their visual styling and only need the semantic
  // <button> element for keyboard / a11y / role="listitem" use.
  bare: "",
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  s: "btn--s",
  xs: "btn--xs",
};

export function Button({
  variant = "secondary",
  size,
  className,
  children,
  type = "button",
  loading = false,
  disabled,
  ...rest
}: ButtonProps): JSX.Element {
  const variantClass = VARIANT_CLASS[variant];
  const sizeClass = size !== undefined ? SIZE_CLASS[size] : "";
  const composed = [
    variantClass,
    sizeClass,
    loading ? "is-loading" : "",
    className,
  ]
    .filter((s) => s && s.length > 0)
    .join(" ");
  return (
    <button
      type={type}
      className={composed}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <Spinner size={14} className="btn__spin" label="Working" />}
      {children}
    </button>
  );
}
